import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MENU } from "../data/menu";
import { LINKS } from "../config";
import { Heart } from "./Logo";
import { useLang } from "../i18n";

export const MenuSection = () => {
  const { t, isAr } = useLang();
  const [active, setActive] = useState(MENU[0].id);
  const category = MENU.find((c) => c.id === active);

  return (
    <section id="menu" className="py-24 md:py-32 bg-parchment border-y border-line" data-testid="menu-section">
      <div className="px-6 md:px-12 max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-wrap items-end justify-between gap-6 mb-14"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-gold font-semibold mb-4">{t.menu.kicker}</p>
            <h2 className="font-cinzel font-medium text-3xl md:text-5xl text-pine tracking-wide">
              {t.menu.titleA} <span className="font-display italic font-light text-gold-shimmer tracking-tight">{t.menu.titleB}</span>
            </h2>
          </div>
          <p className={`${isAr ? "font-display italic" : "font-arabic"} text-gold/80 text-2xl hidden md:block`}>{t.menu.side}</p>
        </motion.div>

        <div className="flex flex-wrap gap-x-8 gap-y-3 border-b border-pine/20 mb-4" data-testid="menu-tabs">
          {MENU.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              data-testid={`menu-tab-${c.id}`}
              className={`relative pb-4 font-cinzel text-[11px] md:text-xs tracking-[0.18em] uppercase transition-colors duration-300 ${
                active === c.id ? "text-pine font-semibold" : "text-smoke hover:text-pine"
              }`}
            >
              {isAr ? c.arabic : c.label}
              {active === c.id && (
                <motion.span
                  layoutId="menu-underline"
                  className="absolute left-0 right-0 -bottom-px h-[2px] bg-gold"
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                />
              )}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-10 text-sm text-smoke" data-testid="menu-category-note">
          <span className={`${isAr ? "font-body" : "font-arabic"} text-gold text-lg`}>{isAr ? category.label : category.arabic}</span>
          <span className="w-1 h-1 rotate-45 bg-heart/60 inline-block" />
          <span>{isAr ? category.noteAr : category.note}</span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active + (isAr ? "-ar" : "")}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="grid md:grid-cols-2 gap-x-16"
            data-testid="menu-items-list"
          >
            {category.items.map((item, i) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                className="py-5 border-b border-pine/15 group"
                data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="flex items-baseline gap-3">
                  <h3 className="font-display text-xl text-pine tracking-wide group-hover:text-gold transition-colors duration-300 shrink-0 flex items-center gap-2 max-w-[70%] flex-wrap">
                    {item.pick && <Heart className="w-3 h-3 shrink-0" />}
                    {isAr ? item.ar : item.name}
                    <span className={`${isAr ? "font-body text-sm" : "font-arabic text-base"} text-gold/80 tracking-normal`}>
                      {isAr ? item.name : item.ar}
                    </span>
                  </h3>
                  <span className="flex-1 border-b border-dotted border-pine/25" />
                  <span className="text-end shrink-0">
                    <span className="text-pine font-body font-bold text-sm tracking-wide" dir="ltr">{item.price}</span>
                    <span className="text-[9px] text-smoke uppercase tracking-[0.2em] mx-1.5">{t.menu.currency}</span>
                  </span>
                </div>
                {(item.desc || item.sizes) && (
                  <p className="text-smoke text-xs mt-1.5">
                    {isAr ? item.descAr : item.desc}
                    {item.desc && item.sizes && " · "}
                    {item.sizes && <span className="uppercase tracking-[0.15em]" dir="ltr">{item.sizes}</span>}
                  </p>
                )}
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        <div className="mt-14 flex flex-wrap items-center gap-6">
          <a
            href={LINKS.talabat}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="menu-talabat-button"
            className="bg-pine text-paper text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-none hover:bg-forest transition-colors duration-300"
          >
            {t.menu.orderBtn}
          </a>
          <p className="text-smoke text-sm flex items-center gap-2">
            <Heart className="w-3 h-3" /> {t.menu.picksNote}
          </p>
        </div>
      </div>
    </section>
  );
};
